import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Package, Truck, Store, Mail, Loader2, Star } from "lucide-react";
import { api, money } from "@/lib/api";
import { BRAND } from "@/data/brand";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

function ReviewInvite({ orderNumber }) {
  const [rating, setRating] = useState(5);
  const [author, setAuthor] = useState("");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!author.trim() || text.trim().length < 3) {
      toast.error("Please add your name and a short review.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews/submit", { order_number: orderNumber, author, rating, text });
      setDone(true);
      toast.success(data.message || "Thank you for your review!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (done)
    return (
      <div data-testid="review-thanks" className="mt-8 rounded-lg border border-green-200 bg-green-50 p-6 text-center">
        <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto" />
        <p className="mt-2 font-display text-xl text-navy">Thank you for sharing your experience!</p>
        <p className="text-ink/70 text-sm mt-1">Your review will appear after a quick check.</p>
      </div>
    );

  return (
    <div data-testid="review-invite" className="mt-8 rounded-lg border border-navy/15 bg-white p-6">
      <h2 className="font-display text-2xl text-navy">Enjoying your find? Leave a review</h2>
      <p className="text-ink/70 text-sm mt-1">
        Thank you for shopping with JohnnyMart. After receiving your order, please consider sharing your experience.
      </p>
      <div className="mt-4 flex items-center gap-1" data-testid="review-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => setRating(n)} className="p-0.5">
            <Star className={`w-7 h-7 ${n <= rating ? "fill-gold text-gold" : "text-navy/25"}`} />
          </button>
        ))}
      </div>
      <input data-testid="review-author" value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Your name or initials"
        className="mt-4 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />
      <textarea data-testid="review-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="How was your item and your JohnnyMart experience? Honest feedback welcome."
        className="mt-3 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick resize-none" />
      <button data-testid="review-submit" onClick={submit} disabled={submitting}
        className="mt-3 inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-6 py-3 hover:bg-navy transition-colors disabled:opacity-60">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />} Submit Review
      </button>
    </div>
  );
}

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [verifying, setVerifying] = useState(!!sessionId);

  useEffect(() => {
    api.get(`/orders/${orderNumber}`).then((r) => setOrder(r.data)).catch(() => setNotFound(true));
  }, [orderNumber]);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          clearCart();
          setVerifying(false);
          api.get(`/orders/${orderNumber}`).then((r) => setOrder(r.data)).catch(() => {});
          return;
        }
        if (data.status === "expired" || attempts >= 6) { setVerifying(false); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        if (active) setVerifying(false);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, orderNumber, clearCart]);

  if (notFound)
    return (
      <div className="jm-container py-24 text-center">
        <h1 className="font-display text-4xl text-navy">Order not found</h1>
        <Link to="/shop" className="text-brick font-bold mt-4 inline-block">Back to shop</Link>
      </div>
    );

  if (!order) return <div className="jm-container py-24 text-center text-navy/50">Loading...</div>;

  const pending = order.payment_status === "pending";

  return (
    <div data-testid="order-confirmation-page" className="jm-container py-16 max-w-3xl">
      {verifying && (
        <div data-testid="payment-verifying" className="mb-6 flex items-center justify-center gap-2 rounded-md bg-gold/20 text-navy px-4 py-3 font-semibold">
          <Loader2 className="w-4 h-4 animate-spin" /> Confirming your payment…
        </div>
      )}
      <div className="text-center">
        <CheckCircle2 className="w-16 h-16 text-brick mx-auto" />
        <h1 className="font-display text-4xl sm:text-5xl text-navy mt-4">Thank you!</h1>
        <p className="text-ink/70 mt-2">Your order has been received. Good items, good people, good vibes.</p>
        <p data-testid="order-number" className="mt-4 inline-block bg-cream border border-navy/15 rounded-full px-5 py-2 font-bold text-navy">
          Order {order.order_number}
        </p>
      </div>

      <div className="mt-10 bg-white rounded-lg border border-navy/15 p-6">
        <div className="flex items-center gap-2 text-navy font-display text-xl">
          {order.fulfillment === "pickup" ? <Store className="w-5 h-5 text-brick" /> : <Truck className="w-5 h-5 text-brick" />}
          {order.fulfillment === "pickup" ? "Local Pickup" : "Shipping"}
        </div>
        {order.fulfillment === "pickup" && (
          <div data-testid="pickup-receipt-banner" className={`mt-3 rounded-md p-3 text-sm font-bold ${pending ? "bg-gold/20 text-navy" : "bg-teal-50 border border-teal-200 text-teal-800"}`}>
            LOCAL PICKUP · Shipping: $0.00 · {pending ? "Status: Awaiting payment" : "Status: Paid — Awaiting Pickup"}
          </div>
        )}
        <div className="mt-3 text-sm text-ink/70 space-y-0.5" data-testid="receipt-details">
          <p><b className="text-navy">Customer:</b> {order.customer?.name} · {order.customer?.email}</p>
          <p><b className="text-navy">Order date:</b> {new Date(order.created_at).toLocaleString("en-US", { timeZone: "America/New_York" })} ET</p>
          <p><b className="text-navy">Payment:</b> <span className={pending ? "text-amber-700 font-bold" : "text-green-700 font-bold"}>{pending ? "Pending" : "Paid"}</span>{order.payment_provider ? ` via ${order.payment_provider}` : ""}</p>
        </div>
        {order.fulfillment === "shipping" && order.shipping_address && (
          <p className="mt-2 text-sm text-ink/70">
            {order.shipping_address.line1} {order.shipping_address.line2}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}
          </p>
        )}

        <div className="mt-5 divide-y divide-navy/10">
          {order.items.map((i) => (
            <div key={`${i.product_id}-${i.variant_vid || ""}`} className="flex items-center gap-3 py-3">
              <Package className="w-5 h-5 text-navy/40" />
              <span className="flex-1 text-ink/80">{i.name}{i.variant_label ? ` (${i.variant_label})` : ""} × {i.quantity}</span>
              <span className="font-semibold">{money(i.price * i.quantity)}</span>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-navy/15 space-y-2 text-ink/80">
          <div className="flex justify-between"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
          <div className="flex justify-between"><span>Shipping</span><span>{order.shipping_cost === 0 ? "Free" : money(order.shipping_cost)}</span></div>
          {order.tax > 0 && <div className="flex justify-between"><span>Sales tax</span><span data-testid="confirmation-tax">{money(order.tax)}</span></div>}
          {order.discount > 0 && <div className="flex justify-between"><span>Discount</span><span>- {money(order.discount)}</span></div>}
          <div className="flex justify-between font-display text-xl text-navy pt-1"><span>Total</span><span>{money(order.total)}</span></div>
        </div>

        <div className={`mt-5 rounded-md p-4 text-sm ${pending ? "bg-gold/20 text-navy" : "bg-cream text-navy"}`}>
          {pending
            ? "Payment status: Pending. Complete your payment to confirm this order — your items are not reserved until payment is received."
            : order.fulfillment === "pickup"
              ? "Payment received — your item is reserved for you. We'll email you to arrange a pickup time in Ponte Vedra Beach, FL."
              : "Payment received. We'll be in touch with next steps shortly."}
        </div>
      </div>

      {!pending && <ReviewInvite orderNumber={order.order_number} />}

      <div className="mt-8 text-center">
        <a href={`mailto:${BRAND.email}?subject=${encodeURIComponent("Order " + order.order_number)}`} className="inline-flex items-center gap-2 text-navy font-bold hover:text-brick">
          <Mail className="w-4 h-4" /> Questions about this order? Email us
        </a>
        <div className="mt-6">
          <Link to="/shop" className="bg-brick text-white font-bold uppercase rounded-md px-6 py-3.5 hover:bg-navy transition-colors">
            Keep Shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
